import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConnectionService } from '../../service/connection.service';
import { SwalService } from '../../service/swal.service';
import { ClearZeroOnFocusDirective } from '../../utility/clear-zero-on-focus.directive';
import { IconComponent } from '../../components/icon/icon.component';
import { LoadErrorComponent } from '../../components/load-error/load-error.component';
import { isDiscountActive } from '../../utility/service-price';
import { AWAY_RELOAD_MS, onReturnAfter } from '../../utility/foreground';

@Component({
  selector: 'app-listino',
  imports: [CommonModule, FormsModule, TranslatePipe, ClearZeroOnFocusDirective, IconComponent, LoadErrorComponent],
  templateUrl: './listino.component.html',
  styleUrl: './listino.component.css',
})
export class ListinoComponent implements OnInit, OnDestroy {
  private connection = inject(ConnectionService);
  private swal = inject(SwalService);
  private router = inject(Router);
  private translate = inject(TranslateService);

  services: any[] = [];
  editingServiceIndex = -1;
  loaded = false;
  // Listino non arrivato: niente modifiche, un salvataggio sovrascriverebbe quello vero
  loadError = false;
  showChainBanner = false;
  private stopReturnWatch?: () => void;

  ngOnInit(): void {
    this.load();
    // PWA ripresa dopo un po': rilegge, se non si sta modificando una voce
    this.stopReturnWatch = onReturnAfter(AWAY_RELOAD_MS, () => {
      if (this.editingServiceIndex === -1) this.load();
    });
    if (localStorage.getItem('listino_intro_pending') === 'true') {
      this.showChainBanner = true;
    }
  }

  ngOnDestroy(): void {
    this.stopReturnWatch?.();
  }

  load(): void {
    // auth/profile è cacheato: riusa i dati già caricati altrove
    this.connection.get(
      'auth/profile',
      (data: any) => {
        this.services = data?.services ?? [];
        this.loadError = false;
        this.loaded = true;
      },
      () => {
        // Una rilettura fallita lascia visibile il listino già caricato
        this.loadError = !this.services.length;
        this.loaded = true;
      },
    );
  }

  isDiscounted(s: any): boolean {
    return isDiscountActive(s);
  }

  toggleDiscount(s: any, active: boolean): void {
    s.discount = { active, price: s.discount?.price ?? 0, until: s.discount?.until ?? '', name: s.discount?.name ?? '' };
  }

  goHome(): void {
    localStorage.removeItem('listino_intro_pending');
    this.router.navigate(['/home']);
  }

  dismissBanner(): void {
    localStorage.removeItem('listino_intro_pending');
    this.showChainBanner = false;
  }

  addService(): void {
    // Nuova voce in cima alla lista
    this.services.unshift({
      name: '',
      price: 0,
      description: '',
      default_active: true,
    });
    this.editingServiceIndex = 0;
  }

  // Voce "vuota" = appena aggiunta e non compilata
  private isEmptyService(s: any): boolean {
    return !s?.name?.trim() && !s?.description?.trim() && !s?.price;
  }

  removeService(i: number): void {
    // Voce vuota → rimuovi senza conferma né salvataggio (non è mai stata salvata)
    if (this.isEmptyService(this.services[i])) {
      this.services.splice(i, 1);
      if (this.editingServiceIndex === i) this.editingServiceIndex = -1;
      return;
    }
    this.swal
      .confirm(
        this.translate.instant('profile.listino.remove_title'),
        this.translate.instant('profile.listino.remove_text'),
      )
      .then((r) => {
        if (r.isConfirmed) {
          this.services.splice(i, 1);
          if (this.editingServiceIndex === i) this.editingServiceIndex = -1;
          this.saveServices();
        }
      });
  }

  // Annulla la modifica: se la voce è vuota (nuova) la toglie dalla lista
  cancelEdit(i: number): void {
    if (this.isEmptyService(this.services[i])) {
      this.services.splice(i, 1);
    }
    this.editingServiceIndex = -1;
  }

  saveServiceItem(i: number): void {
    if (!this.services[i].name?.trim()) {
      this.swal.error(this.translate.instant('profile.listino.name_required'));
      return;
    }
    const d = this.services[i].discount;
    if (d?.active && (!d.until || !(d.price >= 0) || d.price >= this.services[i].price)) {
      this.swal.error(this.translate.instant('profile.listino.discount_invalid'));
      return;
    }
    this.editingServiceIndex = -1;
    this.saveServices();
  }

  saveServices(): void {
    this.connection.put(
      'auth/services',
      { services: this.services },
      () => this.swal.success(this.translate.instant('profile.listino.saved')),
      () => this.swal.errorDialog(
        this.translate.instant('swal.error'),
        this.translate.instant('profile.listino.save_error'),
      ),
    );
  }

}
